"use client";

import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { ConvexReactClient } from "convex/react";
import { ReactNode, useMemo, useEffect, useRef } from "react";

const RECONNECT_DEBOUNCE_MS = 1000;
// After being hidden for this long, force reconnection even if state says connected
// (connection is likely stale after Mac sleep, etc.)
const STALE_CONNECTION_THRESHOLD_MS = 30_000;

export function ConvexClientProvider({ children }: { children: ReactNode }) {
  const convex = useMemo(
    () => new ConvexReactClient(process.env.NEXT_PUBLIC_CONVEX_URL!),
    []
  );

  const lastReconnectAttempt = useRef<number>(0);
  const hiddenSince = useRef<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const timestamp = new Date().toISOString();

      if (document.visibilityState === "hidden") {
        // Record when the page became hidden
        hiddenSince.current = Date.now();
        console.log(`[${timestamp}] Convex: Page hidden, recording timestamp`);
        return;
      }

      // Page became visible - trigger reconnection check
      handleReconnect("visibilitychange", timestamp);
    };

    const handleReconnect = (eventType: string, timestamp?: string) => {
      const ts = timestamp ?? new Date().toISOString();
      console.log(`[${ts}] Convex: ${eventType} event triggered`);

      // Check if page is actually visible
      if (document.visibilityState !== "visible") {
        console.log(`[${ts}] Convex: Page not visible, skipping reconnection`);
        return;
      }

      // Debounce: Don't attempt reconnection if we tried recently
      const now = Date.now();
      const timeSinceLastAttempt = now - lastReconnectAttempt.current;
      if (timeSinceLastAttempt < RECONNECT_DEBOUNCE_MS) {
        console.log(
          `[${ts}] Convex: Debouncing, last attempt ${timeSinceLastAttempt}ms ago`
        );
        return;
      }

      // Check if page was hidden for a long time (e.g., Mac sleep)
      // If so, force reconnection regardless of reported state (connection may be stale)
      const wasHiddenFor = hiddenSince.current
        ? now - hiddenSince.current
        : 0;
      const forceReconnect = wasHiddenFor > STALE_CONNECTION_THRESHOLD_MS;

      // Check connection state
      const state = convex.connectionState();
      console.log(`[${ts}] Convex: Connection state:`, {
        isWebSocketConnected: state.isWebSocketConnected,
        hasInflightRequests: state.hasInflightRequests,
        wasHiddenForMs: wasHiddenFor,
        forceReconnect,
      });

      // Clear hidden timestamp now that we're visible
      hiddenSince.current = null;

      // Attempt reconnection if disconnected OR if connection is likely stale
      // Note: We don't check hasInflightRequests because pending mutations
      // actually NEED the connection to be re-established
      if (!state.isWebSocketConnected || forceReconnect) {
        lastReconnectAttempt.current = now;
        console.log(
          `[${ts}] Convex: Attempting reconnection...${forceReconnect ? " (forced due to long hidden period)" : ""}`
        );
        // Accessing connectionState() triggers the client to check connection status
        convex.connectionState();
      } else {
        console.log(`[${ts}] Convex: Already connected, skipping`);
      }
    };

    const handleFocusOrOnline = (event: Event) => {
      handleReconnect(event.type);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocusOrOnline);
    window.addEventListener("online", handleFocusOrOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocusOrOnline);
      window.removeEventListener("online", handleFocusOrOnline);
    };
  }, [convex]);

  return <ConvexAuthProvider client={convex}>{children}</ConvexAuthProvider>;
}